import ConfirmationModal from '../../../components/confirmation-modal';
import PluginAction from '../../plugin-action';

/**
 * Action for listing offers of subpages of a checklist step.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export default class SubpagesFormAction extends PluginAction {
    /**
     * Form containing the chosen subpage settings.
     */
    private readonly form: HTMLFormElement;

    /**
     * Contains all status radio buttons.
     */
    private readonly statusRadios: NodeListOf<HTMLInputElement>;

    constructor(container: HTMLElement) {
        super(container);
        const form = this.container.querySelector<HTMLFormElement>('#checklist-step-form');
        const statusRadios = this.container.querySelectorAll<HTMLInputElement>('[name="tx_sitepackage_subpagescheckliststep[checklistStep][status]"]');
        // Check for mandatory elements; side offers or price info fields may not be present
        if (!form || !statusRadios || statusRadios.length === 0) {
            throw new Error('Required elements missing.');
        }
        this.form = form;
        this.statusRadios = statusRadios;

        // If this is a fresh step, not yet saved, then select the first status by default
        if (!this.getSelectedStatusRadio()) {
            this.statusRadios.item(0).checked = true;
        }

        this.initializeForm();
        this.initializeModals();
        SubpagesFormAction.emitter.emit('ready', this);
    }

    /**
     * Gets the currently selected status radio button.
     *
     * @returns {HTMLInputElement|null}
     */
    private getSelectedStatusRadio(): HTMLInputElement | null {
        for (const item of this.statusRadios) {
            if (item.checked) {
                return item;
            }
        }
        return null;
    }

    /**
     * Initializes the form with the status radio buttons. The idea is the same as with the OfferFormAction, but much simpler and adjusted for this use case.
     */
    private initializeForm(): void {
        this.statusRadios.forEach(item => {
            const value = parseInt(item.value);

            if (item.checked) {
                this.showElementsWithStatus(value);
            }

            item.addEventListener('change', () => {
                this.showElementsWithStatus(value);
            });
        });
    }

    /**
     * Initializes all modals for confirming certain actions like deleting offers.
     */
    private initializeModals(): void {
        // Initialize modal to remove an offer
        const removeModal = document.querySelector<HTMLElement>('#removeModal');
        if (removeModal) {
            const removeConfirmation = new ConfirmationModal(removeModal);
            removeConfirmation.emitter.on('confirm', (link: HTMLElement) => {
                const href = link.getAttribute('href');
                if (href) {
                    window.location.href = href;
                }
            });

            this.container.querySelectorAll<HTMLElement>('[data-action="remove"]').forEach(link => {
                link.addEventListener('click', e => {
                    e.preventDefault();
                    removeConfirmation.value = link;
                    removeConfirmation.show();
                });
            });
        }

        // Initialize modal for when the status changes
        const statusChangedRemoveAllModal = document.querySelector<HTMLElement>('#statusChangedRemoveAllModal');
        if (statusChangedRemoveAllModal) {
            const statusChangedRemoveAllConfirmation = new ConfirmationModal(statusChangedRemoveAllModal);
            statusChangedRemoveAllConfirmation.emitter.on('confirm', () => {
                this.form.submit();
            });

            this.form.addEventListener('submit', (e) => {
                // Form was confirmed, allow normal submitting
                if (statusChangedRemoveAllConfirmation.value) {
                    return;
                }

                const selectedStatusRadio = this.getSelectedStatusRadio();
                if (selectedStatusRadio && selectedStatusRadio.value !== '3') {
                    e.preventDefault();
                    statusChangedRemoveAllConfirmation.value = true; // Set to some value, to allow submitting the form afterwards
                    statusChangedRemoveAllConfirmation.show();
                }
            });
        }
    }

    /**
     * Shows all elements fitting the given status and hides any other status element. The status is checked via a `data-status` attribute.
     *
     * @param {number} status Status of the elements to show
     */
    private showElementsWithStatus(status: number): void {
        this.container.querySelectorAll<HTMLElement>('[data-status]').forEach(element => {
            if (!element.dataset.status) {
                return;
            }

            const elementStatus = parseInt(element.dataset.status);
            if (status === elementStatus) {
                element.classList.remove('d-none');
            } else {
                element.classList.add('d-none');
            }
        });
    }
}
